import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  churchName: PropTypes.string,
  departmentName: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

// Define individual Cell components with PropTypes
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

// Create a factory function for ChurchCell that receives the church object
const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar name={value} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {value}
        </MDTypography>
        <MDTypography variant="caption">{church?.email || "church@example.com"}</MDTypography>
      </MDBox>
    </MDBox>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const DepartmentCell = ({ value, row }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="button" fontWeight="medium">
      {value}
    </MDTypography>
    <MDTypography variant="caption" color="text">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </MDTypography>
  </MDBox>
);
DepartmentCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useDepartmentData() {
  const { user, church } = useAuth();

  // Create ChurchCell with access to the church object
  const ChurchCell = createChurchCell(church);

  const [departmentData, setDepartmentData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Church",
        accessor: "churchName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Department",
        accessor: "departmentName",
        align: "left",
        width: "25%",
        Cell: DepartmentCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setEditModalOpen(true);
  };

  const handleDelete = (department) => {
    setSelectedDepartment(department);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedDepartment.id
        ? `https://api.ofi-cia.com/api/departments/${selectedDepartment.id}`
        : "https://api.ofi-cia.com/api/departments";

      const method = selectedDepartment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedDepartment.departmentName,
          status: selectedDepartment.status === "active",
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      Swal.fire({
        title: "Success!",
        text: `Department successfully saved`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchDepartments();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Department operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      //   setDepartmentData((prev) => ({
      //     ...prev,
      //     error: error.message,
      //   }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        `https://api.ofi-cia.com/api/departments/${selectedDepartment.id}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Department, ${selectedDepartment.departmentName} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchDepartments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setDepartmentData((prev) => ({
        ...prev,
        error: error.message,
      }));
    }
  };

  const handleAddDepartment = () => {
    setSelectedDepartment({
      id: null,
      departmentName: "",
      status: "active",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/departments/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const departments = result.data || result.departments || result;

      if (!Array.isArray(departments)) {
        throw new Error("API response is not an array");
      }

      setDepartmentData((prev) => ({
        ...prev,
        rows: departments.map((dept, index) => ({
          id: dept.id,
          serial: index + 1,
          churchName: church.name,
          departmentName: dept.name || dept.department_name,
          status: dept.status ? "active" : "inactive",
          createdAt: dept.created_at,
          originalData: dept,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setDepartmentData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [user, church]);

  return {
    ...departmentData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDepartment,
    setSelectedDepartment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDepartment,
  };
}
